import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import useChargebackStore from "../../../../stores/chargeback";
import style from "./styles.module.scss";
import {
  getChargebackPageVolumeByMonth,
  getChargebackVolume,
} from "../../../../../api";
import moment from "moment";
import { toast } from "react-hot-toast";
import SkeletonLoader from "../../../../components/skeleton-loader";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";

// Helper function to generate labels based on rangeType
// ToDo: REMOVE THIS POST DEMO. DROP APEX.
const generateLabels = (rangeType, startDate, endDate) => {
  if (!startDate || !endDate) return [];

  const start = moment(startDate);
  const end = moment(endDate);
  const startYear = start.year();

  switch (rangeType) {
    case "MTD":
    case "MONTH": {
      // Sub-week ranges (early MTD) collapse to a single weekly bucket — show daily.
      if (end.diff(start, "days") + 1 < 7) {
        const labels = [];
        const current = start.clone();
        while (current.isSameOrBefore(end)) {
          const full = current.format("D MMM, YY");
          labels.push({
            day: current.clone(),
            label: full,
            shortLabel: current.format("D MMM"),
          });
          current.add(1, "day");
        }
        return labels;
      }

      // Generate weekly labels for month-to-date or month range
      const labels = [];
      const current = start.clone();

      while (current.isSameOrBefore(end)) {
        const weekStart = current.clone().startOf("week");
        const weekEnd = current.clone().endOf("week");

        // Adjust week start to Monday if needed
        if (weekStart.day() === 0) weekStart.add(1, "day");
        if (weekEnd.day() === 0) weekEnd.subtract(1, "day");

        const full = `${weekStart.format("D")}-${weekEnd.format("D MMM, YY")}`;
        labels.push({
          weekStart,
          weekEnd,
          label: full,
          shortLabel: `${weekStart.format("D")}-${weekEnd.format("D MMM")}`,
        });

        current.add(1, "week");
      }

      return labels;
    }

    case "YTD": {
      const labels = [];
      const yearStart = moment(startYear, "YYYY").startOf("year");
      const current = yearStart.clone();

      while (current.isSameOrBefore(end)) {
        const full = current.format("MMM, YY");
        labels.push({
          monthStart: current.clone(),
          monthEnd: current.clone().endOf("month"),
          label: full,
          shortLabel: current.format("MMM"),
        });

        current.add(1, "month");
      }

      return labels;
    }

    case "LTY": {
      const lastYear = moment().year() - 1;
      const labels = [];
      const yearStart = moment(lastYear, "YYYY").startOf("year");
      const yearEnd = moment(lastYear, "YYYY").endOf("year");
      const current = yearStart.clone();

      while (current.isSameOrBefore(yearEnd)) {
        const full = current.format("MMM, YY");
        labels.push({
          monthStart: current.clone(),
          monthEnd: current.clone().endOf("month"),
          label: full,
          shortLabel: current.format("MMM"),
        });

        current.add(1, "month");
      }

      return labels;
    }

    default:
      // Fallback to monthly labels for the year
      return Array.from({ length: 12 }, (_, i) => {
        const m = moment().month(i).year(startYear);
        const full = m.format("MMM YYYY");
        return {
          monthStart: m,
          monthEnd: m.clone().endOf("month"),
          label: full,
          shortLabel: m.format("MMM"),
        };
      });
  }
};

function ChargebackVolumeChart() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    transactions: [],
    chargebacks: [],
  });
  const isNarrowChart = useMediaQuery(768);
  const volumeFilter = useChargebackStore((state) => state.volumeFilter);
  const filters = useChargebackStore((state) => state.filters);

  const ratios = useMemo(() => {
    return data.transactions.map((trans, index) => {
      if (!trans) return 0;
      return (data.chargebacks[index] / trans) * 100;
    });
  }, [data.transactions, data.chargebacks]);

  const dynamicLabels = useMemo(() => {
    const rangeType = filters.dateRange?.rangeType || "MTD";
    const startDate = filters.dateRange?.startDate;
    const endDate = filters.dateRange?.endDate;

    return generateLabels(rangeType, startDate, endDate);
  }, [filters.dateRange]);

  const axisLabels = useMemo(() => {
    return (
      dynamicLabels?.map((entry) =>
        isNarrowChart && entry.shortLabel ? entry.shortLabel : entry.label,
      ) || []
    );
  }, [dynamicLabels, isNarrowChart]);

  const tooltipLabels = useMemo(() => {
    return dynamicLabels?.map((entry) => entry.label) || [];
  }, [dynamicLabels]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Extract year from the date range, default to current year if not set
        const year = filters.dateRange?.startDate
          ? moment(filters.dateRange.startDate).year()
          : moment().year();

        const params = {
          type: volumeFilter.type.toLowerCase(),
          year,
          start_date: filters.dateRange?.startDate,
          end_date: filters.dateRange?.endDate,
          rangeType: filters.dateRange?.rangeType,
        };

        const response = await getChargebackVolume(params);

        if (response?.status === true && response?.data) {
          setData({
            transactions: response.data.transactions || [],
            chargebacks: response.data.chargebacks || [],
          });
        } else {
          toast.error("Failed to fetch chargeback volume data");
          setData({
            transactions: [],
            chargebacks: [],
          });
        }
      } catch (error) {
        toast.error("Error fetching chargeback volume data");
        setData({
          transactions: [],
          chargebacks: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [volumeFilter.type, filters.dateRange]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 350,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        fontFamily: "Work Sans, sans-serif",
      },
      colors: ["#4C70EF", "#FF4842"],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0,
          opacityFrom: 0.6,
          opacityTo: 0.2,
          stops: [0, 100],
        },
      },
      xaxis: {
        categories: axisLabels,
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          style: {
            colors: "#667085",
            fontSize: isNarrowChart ? "10px" : "12px",
            fontFamily: "Work Sans",
          },
          rotate: 0,
          hideOverlappingLabels: isNarrowChart,
          showDuplicates: false,
          trim: isNarrowChart,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#667085",
            fontSize: "12px",
            fontFamily: "Work Sans",
          },
          formatter: (value) => {
            return volumeFilter.type.toLowerCase() == "volume"
              ? value.toLocaleString()
              : `$ ${value.toLocaleString()}`;
          },
        },
        tickAmount: 6,
      },
      grid: {
        borderColor: "#F0F1F3",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 10,
        },
      },
      tooltip: {
        custom: function ({ series, dataPointIndex }) {
          const date = tooltipLabels[dataPointIndex];
          const transactions = series[0][dataPointIndex];
          const chargebacks = series[1][dataPointIndex];
          const ratio =
            transactions && transactions > 0
              ? ((chargebacks / transactions) * 100).toFixed(1)
              : "0.0";

          return `
          <div class="${style.tooltip}">
            <div class="${style.tooltipHeader}">${date}</div>
            <div class="${style.tooltipContent}">
              <div class="${style.tooltipRow}">
                <div class="${style.tooltipLabel}">
                  <span class="${style.tooltipDot}" style="background: #4C70EF"></span>
                  Transactions:
                </div>
                <div class="${style.tooltipValue}">
                  ${transactions.toLocaleString()}
                </div>
              </div>
              <div class="${style.tooltipRow}">
                <div class="${style.tooltipLabel}">
                  <span class="${style.tooltipDot}" style="background: #FF4842"></span>
                  Chargebacks:
                </div>
                <div class="${style.tooltipValue}">
                  ${
                    volumeFilter.type === "volume"
                      ? chargebacks.toLocaleString()
                      : `<span style="color: #6E6D7A">${ratio}%</span> $${chargebacks.toLocaleString()}`
                  }
                </div>
              </div>
            </div>
          </div>
        `;
        },
        shared: true,
        intersect: false,
        fixed: {
          enabled: true,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
      },
      legend: {
        show: false,
      },
    }),
    [axisLabels, tooltipLabels, volumeFilter.type, isNarrowChart],
  );

  const series = useMemo(
    () => [
      {
        name: "Transactions",
        data: data.transactions,
      },
      {
        name: "Chargebacks",
        data: data.chargebacks,
      },
    ],
    [data],
  );

  if (loading) {
    return (
      <div className={style.volumeChart}>
        <SkeletonLoader variant="rectangular" width="100%" height={350} />
      </div>
    );
  }

  return (
    <div className={style.volumeChart}>
      <Chart options={chartOptions} series={series} type="area" height={350} />
    </div>
  );
}

export default ChargebackVolumeChart;
